/**
 * Custom Filter Toggle & Sort Dropdown
 */

class CustomFilterToggle extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.custom-filter-toggle');
    this.collectionContent = this.closest('.collection__content');

    if (this.button) {
      this.button.addEventListener('click', this.toggle.bind(this));
    }

    // Close button inside sidebar
    const closeBtn = this.collectionContent?.querySelector('.custom-filter-sidebar__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.close.bind(this));
    }

    // Backdrop click to close
    const backdrop = this.collectionContent?.querySelector('.custom-filter-sidebar-backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', this.close.bind(this));
    }
  }

  toggle() {
    if (this.collectionContent) {
      const isOpen = this.collectionContent.classList.contains('filter-sidebar-open');
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  open() {
    if (this.collectionContent) {
      this.collectionContent.classList.add('filter-sidebar-open');
      this.button.setAttribute('aria-expanded', 'true');
      // Prevent body scroll on mobile
      if (window.innerWidth <= 767) {
        document.body.style.overflow = 'hidden';
      }
    }
  }

  close() {
    if (this.collectionContent) {
      this.collectionContent.classList.remove('filter-sidebar-open');
      this.button.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  }
}

customElements.define('custom-filter-toggle', CustomFilterToggle);

class CustomSortDropdown extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.custom-sort-button');
    this.dropdown = this.querySelector('.custom-sort-dropdown');
    this.items = this.querySelectorAll('.custom-sort-dropdown__item');
    this.headerClose = this.querySelector('[data-sort-close]');

    if (this.button) {
      this.button.addEventListener('click', this.toggleDropdown.bind(this));
    }

    if (this.headerClose) {
      this.headerClose.addEventListener('click', this.closeDropdown.bind(this));
    }

    this.items.forEach((item) => {
      item.addEventListener('click', this.onItemClick.bind(this));
    });

    // Close dropdown on outside click
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeDropdown();
      }
    });
  }

  toggleDropdown() {
    const isOpen = this.dropdown.classList.contains('is-open');
    if (isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.dropdown.classList.add('is-open');
    this.classList.add('is-open-state');
    this.button.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    this.dropdown.classList.remove('is-open');
    this.classList.remove('is-open-state');
    this.button.setAttribute('aria-expanded', 'false');
  }

  onItemClick(event) {
    const value = event.target.dataset.value;
    const label = event.target.textContent.trim();

    // Update active state
    this.items.forEach((item) => item.classList.remove('is-active'));
    event.target.classList.add('is-active');

    // Update button label + header label
    const buttonLabel = this.button.querySelector('.custom-sort-button__label');
    if (buttonLabel) {
      buttonLabel.textContent = label;
    }
    const headerLabel = this.querySelector('.custom-sort-dropdown__header-label');
    if (headerLabel) {
      headerLabel.textContent = label;
    }

    this.closeDropdown();

    // Trigger the sort change via the facet form
    const form = document.querySelector('form[is="facet-form"]');
    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }
}

customElements.define('custom-sort-dropdown', CustomSortDropdown);

/**
 * Boolean filter toggle handler
 * Handles "表示/非表示" toggle buttons for boolean filters (e.g., SOLD OUT)
 */
document.addEventListener('click', function (e) {
  const btn = e.target.closest('[data-filter-boolean]');
  if (!btn) return;

  const paramName = btn.dataset.param;
  const value = btn.dataset.value;
  const container = btn.closest('.custom-filter-boolean');
  const buttons = container.querySelectorAll('.custom-filter-boolean__btn');

  // Toggle active state
  const wasActive = btn.classList.contains('is-active');
  buttons.forEach((b) => b.classList.remove('is-active'));
  if (!wasActive) {
    btn.classList.add('is-active');
  }

  // Trigger filter change via the facet form
  const form = document.querySelector('form[is="facet-form"]');
  if (form) {
    const url = new URL(window.location.href);

    // Remove existing values for this param
    url.searchParams.delete(paramName);

    // If clicking a button that wasn't active, set its value
    if (!wasActive) {
      url.searchParams.set(paramName, value);
    }

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
    url.searchParams.delete('page');
    form.renderSection(url.toString(), e);
  }
});

/**
 * Mobile sort checkboxes inside sidebar
 * Acts like radio buttons (only one active at a time)
 */
document.addEventListener('change', function (e) {
  const checkbox = e.target.closest('.custom-mobile-sort__checkbox');
  if (!checkbox) return;

  const value = checkbox.dataset.sortValue;
  const container = checkbox.closest('.custom-mobile-sort__list');
  const allCheckboxes = container.querySelectorAll('.custom-mobile-sort__checkbox');

  // Uncheck all others (radio-like behavior)
  allCheckboxes.forEach((cb) => {
    if (cb !== checkbox) cb.checked = false;
  });

  // Ensure at least one is checked
  if (!checkbox.checked) {
    checkbox.checked = true;
    return;
  }

  // Also update desktop sort dropdown active state
  const sortDropdown = document.querySelector('custom-sort-dropdown');
  if (sortDropdown) {
    const items = sortDropdown.querySelectorAll('.custom-sort-dropdown__item');
    items.forEach((item) => {
      item.classList.toggle('is-active', item.dataset.value === value);
    });
    const buttonLabel = sortDropdown.querySelector('.custom-sort-button__label');
    const headerLabel = sortDropdown.querySelector('.custom-sort-dropdown__header-label');
    const activeItem = sortDropdown.querySelector(`.custom-sort-dropdown__item[data-value="${value}"]`);
    if (activeItem) {
      if (buttonLabel) buttonLabel.textContent = activeItem.textContent.trim();
      if (headerLabel) headerLabel.textContent = activeItem.textContent.trim();
    }
  }

  // Trigger sort via facet form
  const form = document.querySelector('form[is="facet-form"]');
  if (form) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
    url.searchParams.delete('page');
    form.renderSection(url.toString(), e);
  }
});
